import * as consts from '../utils/constants';

/**
 * Shared route manifest.
 *
 * Pure data plus pure functions of auth state — no component references, no
 * router imports, no icon components. Each app pairs this with its own screen
 * registry keyed by `slug`:
 *
 *   apps/web/src/routes/screens.js     slug -> React.lazy(() => import(...))
 *   apps/native/src/routes/screens.js  slug -> RN screen component
 *
 * A slug missing from a registry simply does not mount on that platform, which
 * is how native opts out of web-only pages like `privacy-policy`.
 *
 * Entry shape:
 *   slug          Stable identity. The join key between manifest and registry.
 *   path          Web-style path (`:id`). `toNativePath()` converts to `[id]`.
 *   title         Document/screen title.
 *   label         Human label for nav.
 *   icon          Icon *name*, not a component. Resolved per platform.
 *   canAccess     Boolean or () => boolean. Read via `checkRouteAccess`.
 *   showInSideNav / showInNav / showInFooter
 *   showBreadcrumbs  `false` hides the breadcrumb bar on this page. Pages
 *                 with no ancestors never show one, so this is only needed
 *                 on nested pages that want it gone.
 *   redirectTo    Auth-gate bounce: this route is not reachable in the current
 *                 auth state, send the visitor to this slug instead. The web
 *                 app preserves a `back_url` so login can return them.
 *   aliasOf       This path is a short alias for another slug; params carry
 *                 over. Logged in, `/v/7` resolves to `/viewings/7`; logged
 *                 out, it becomes login with `back_url=/viewings/7`.
 *   parent        Slug of the entry this page sits under, for breadcrumbs
 *                 and back-titles. Only needed where the path prefix is
 *                 not the parent: `/profile/credit_reports/...` is reached
 *                 from My Data, not Profile. See `routeTrail()` in index.js.
 *   platforms     Optional allow-list. Omitted means "both".
 *
 * `redirectTo` and `aliasOf` replace the web `Navigate*Feature` components,
 * which were URL-rewrite shims rather than real pages. The manifest states the
 * intent; each platform owns the mechanism.
 *
 * ORDER IS SIGNIFICANT. Two entries deliberately share a path
 * (`application_submit` vs `application_submit_unverified`) and rely on
 * first-match-wins, so these must stay ordered arrays, not maps.
 */

/** @type {'web'} */
export const PLATFORM_WEB = 'web';

/** @type {'native'} */
export const PLATFORM_NATIVE = 'native';

/**
 * Homepage
 *
 * Unverified tenants get the nudge page.
 * Verified tenants with a property are *redirected* to the property list
 * rather than having it rendered in place the address bar reflects where they are.
 * Verified without a property lands on
 * welcome.
 *
 * Takes the same `showProperties` the side nav is built from, so the page a
 * tenant lands on and the nav entry they see cannot disagree.
 *
 * @param {{verified: boolean, showProperties: boolean}} args
 * @returns {{screen: string}|{redirectTo: string}} Partial route entry.
 */
const homeTarget = ({verified, showProperties}) => {
  if (!verified) {
    return {screen: 'home_non_verified'};
  }
  return showProperties ? {redirectTo: 'properties'} : {screen: 'home_welcome'};
};

/**
 * Routes available once logged in.
 *
 * @param {object} args
 * @param {boolean} args.fetching - Auth state still resolving.
 * @param {boolean} args.loggedIn
 * @param {boolean} args.userVerified
 * @param {boolean} args.canAccessSelfCheck - Unleash flag + user allow-list.
 * @param {boolean} args.hasProperties
 * @returns {object[]} Ordered route entries.
 */
export const authenticatedRoutes = ({
  fetching,
  loggedIn,
  userVerified,
  canAccessSelfCheck,
  hasProperties = false,
}) => {
  if (fetching || !loggedIn) {
    return [];
  }

  const verified = userVerified === true;
  /**
   * Swap Home for Properties in the side nav once the tenant has a property.
   *
   * Coerced rather than compared to `true`: `hasProperties` arrives straight
   * off an API response, and a truthy non-boolean (`1`, `[]`) used to read as
   * "has properties" for the redirect below but "none" for the nav, landing
   * new tenants on an empty property list under a Home nav item.
   */
  const showProperties = verified && Boolean(hasProperties);

  return [
    {
      slug: 'home',
      label: 'Home',
      title: 'Home - Tenant Portal',
      path: consts.SLUG_HOME,
      showInSideNav: !showProperties,
      ...homeTarget({verified, showProperties}),
      canAccess: true,
      icon: 'HomeOutlined',
    },
    {
      slug: 'property-details',
      label: '',
      title: 'Properties - Tenant Portal',
      path: `${consts.SLUG_PROPERTIES}/:id`,
      canAccess: verified,
      showInSideNav: false,
      showBreadcrumbs: false,
    },
    {
      slug: 'properties',
      label: 'Properties',
      title: 'Properties - Tenant Portal',
      path: consts.SLUG_PROPERTIES,
      canAccess: verified,
      showInSideNav: showProperties,
      icon: 'HomeOutlined',
    },
    {
      slug: 'viewings',
      label: 'Viewings',
      title: 'Viewings - Tenant Portal',
      path: `${consts.SLUG_VIEWINGS}`,
      url: `${consts.SLUG_VIEWINGS}`,
      canAccess: true,
      showInSideNav: true,
      icon: 'CalendarOutlined',
    },
    {
      slug: 'viewing_details',
      label: 'Viewing Details',
      title: 'Viewing details - Tenant Portal',
      path: `${consts.SLUG_VIEWINGS}/:id`,
      canAccess: true,
    },
    {
      slug: 'applications',
      label: 'Applications',
      title: 'Applications - Tenant Portal',
      path: consts.SLUG_APPLICATIONS,
      canAccess: verified,
      showInSideNav: true,
      icon: 'ApplicationOutlined',
    },
    {
      slug: 'application_complete',
      label: 'Application Complete',
      title: 'Application Submitted',
      path: `${consts.SLUG_APPLICATIONS}/complete`,
      canAccess: verified,
    },
    {
      slug: 'application_overview',
      label: 'Application Overview',
      title: 'Application details - Tenant Portal',
      path: `${consts.SLUG_APPLICATIONS}/:id`,
      canAccess: verified,
    },
    {
      slug: 'application_view',
      label: 'View Application',
      title: 'Application details - Tenant Portal',
      path: `${consts.SLUG_APPLICATIONS}/:id/view`,
      canAccess: verified,
    },
    {
      slug: 'application_submit',
      label: 'Submit Application',
      title: 'Application part 2 - Tenant Portal',
      path: `${consts.SLUG_APPLICATIONS}/:id/submit`,
      canAccess: verified,
    },
    {
      slug: 'agreements',
      label: 'Agreements',
      title: 'Agreements - Tenant Portal',
      path: consts.SLUG_TENANCIES,
      canAccess: verified,
      showInSideNav: true,
      icon: 'AgreementOutlined',
    },
    {
      slug: 'agreement_view',
      label: 'Agreement',
      title: 'Agreement details - Tenant Portal',
      path: `${consts.SLUG_TENANCIES}/:id`,
      canAccess: verified,
    },
    {
      slug: 'agreement_sign',
      label: 'Sign Agreement',
      title: 'Sign Agreement - Tenant Portal',
      path: `${consts.SLUG_TENANCIES}/:id/sign`,
      canAccess: verified,
    },
    {
      slug: 'profile',
      label: 'Profile',
      title: 'Profile - Tenant Portal',
      path: consts.SLUG_PROFILE,
      showInSideNav: true,
      canAccess: verified,
      icon: 'ProfileOutlined',
    },
    {
      slug: 'profile_my_data',
      label: 'My Data',
      title: 'Dashboard - Tenant Portal',
      path: consts.SLUG_PROFILE_MY_DATA,
      canAccess: verified && canAccessSelfCheck,
    },
    {
      slug: 'credit_reports',
      label: 'Credit Reports',
      title: 'My Data',
      path: `${consts.SLUG_PROFILE_CREDIT_REPORTS}/:provider/:id`,
      canAccess: verified && canAccessSelfCheck,
      parent: 'profile_my_data',
    },
    {
      slug: 'profile_upgrade_types',
      label: 'Profile Upgrade',
      path: `${consts.SLUG_PROFILE_UPGRADE}/:type?`,
      canAccess: verified && canAccessSelfCheck,
      parent: 'profile_my_data',
    },
    {
      slug: 'settings',
      label: 'Settings',
      title: 'Settings - Tenant Portal',
      path: consts.SLUG_SETTINGS,
      canAccess: verified,
      showInSideNav: true,
      icon: 'SettingsOutlined',
    },
    {
      slug: 'verify_account',
      label: 'Verify Account',
      title: 'Verify Account - Tenant Portal',
      path: '/auth/verify/:id/:hash',
      canAccess: true,
    },
    {
      slug: 'login_enrol',
      label: 'Secure your account',
      title: 'Secure your account - Tenant Portal',
      path: consts.SLUG_LOGIN_ENROL,
      canAccess: true,
      showInSideNav: false,
      showBreadcrumbs: false,
    },
    {
      slug: 'auth_unverified_login',
      label: 'Login',
      path: consts.SLUG_LOGIN,
      redirectTo: 'home',
      canAccess: !verified,
    },
    {
      slug: 'application_submit_unverified',
      label: 'Submit Application',
      path: `${consts.SLUG_APPLICATIONS}/:id/submit`,
      redirectTo: 'home',
      canAccess: !verified,
    },
    {
      slug: 'viewings_alt',
      label: 'Viewings',
      path: '/v',
      aliasOf: 'viewings',
      canAccess: true,
      platforms: [PLATFORM_WEB],
    },
    {
      slug: 'viewing_details_alt',
      label: 'Viewing Details',
      path: '/v/:id',
      aliasOf: 'viewing_details',
      canAccess: true,
      platforms: [PLATFORM_WEB],
    },
  ];
};

const PROTECTED_SEGMENTS = [
  {slug: 'properties', label: 'Properties', path: consts.SLUG_PROPERTIES},
  {slug: 'viewings', label: 'Viewings', path: consts.SLUG_VIEWINGS},
  {slug: 'applications', label: 'Applications', path: consts.SLUG_APPLICATIONS},
  {slug: 'agreements', label: 'Agreements', path: consts.SLUG_TENANCIES},
  {slug: 'profile', label: 'Profile', path: consts.SLUG_PROFILE},
  {slug: 'settings', label: 'Settings', path: consts.SLUG_SETTINGS},
];

export const unauthenticatedRoutes = ({loggedIn}) => {
  if (loggedIn) {
    return [];
  }

  /** Every protected path bounces an anonymous visitor to login. */
  const toLogin = (slug, label, path) => ({
    slug,
    label,
    path,
    redirectTo: 'login',
  });

  return [
    {
      slug: 'login',
      label: 'Login',
      title: 'Login - Tenant Portal',
      path: consts.SLUG_LOGIN,
    },
    {
      slug: 'login_verify',
      label: 'Verify Login',
      title: 'Verify Login - Tenant Portal',
      path: consts.SLUG_LOGIN_VERIFY,
    },
    {
      slug: 'register',
      label: 'Register',
      title: 'Register - Tenant Portal',
      path: consts.SLUG_REGISTER,
    },
    {
      slug: 'request_reset',
      label: 'Forgot Password',
      title: 'Forgotten Password - Tenant Portal',
      path: consts.SLUG_FORGOTTEN,
    },
    {
      slug: 'verify_reset',
      label: 'Verify Password Reset',
      title: 'Reset Password',
      path: consts.SLUG_VERIFY_FULL_RESET,
    },
    {
      slug: 'reset',
      label: 'Password Reset',
      title: 'Reset Password',
      path: consts.SLUG_RESET,
    },
    toLogin(
      'unauth_verify_account',
      'Verify Account',
      '/auth/verify/:id/:hash'
    ),
    ...PROTECTED_SEGMENTS.map(({slug, label, path}) => ({
      ...toLogin(`unauth_${slug}`, label, `${path}/*`),
      platforms: [PLATFORM_WEB],
    })),
    {
      slug: 'unauth_viewings_alt',
      label: 'Viewings',
      path: '/v',
      aliasOf: 'viewings',
      platforms: [PLATFORM_WEB],
    },
    {
      slug: 'unauth_viewing_details_alt',
      label: 'Viewing Details',
      path: '/v/:id',
      aliasOf: 'viewing_details',
      platforms: [PLATFORM_WEB],
    },
  ];
};

export const publicRoutes = () => [
  {
    slug: 'home',
    label: 'Home',
    title: 'Home - Tenant Portal',
    path: consts.SLUG_HOME,
    showInNav: true,
    screen: 'public_home',
    platforms: [PLATFORM_WEB],
  },
  {
    slug: 'about',
    label: 'About',
    title: 'About Us - Tenant Portal',
    path: consts.SLUG_ABOUT,
    showInNav: true,
  },
  {
    slug: 'privacy',
    label: 'Privacy',
    title: 'Privacy - Tenant Portal',
    path: consts.SLUG_PRIVACY,
    showInNav: true,
    showInFooter: true,
  },
  {
    slug: 'contact',
    label: 'Contact',
    title: 'Contact - Tenant Portal',
    path: consts.SLUG_CONTACT,
    showInNav: true,
    showInFooter: true,
  },
  {
    slug: 'privacy_policy',
    label: 'Privacy Policy',
    title: 'Privacy Policy - Tenant Portal',
    path: consts.URL_PRIVACY_POLICY,
    parent: 'privacy',
    platforms: [PLATFORM_WEB],
  },
  {
    slug: 'request_data_alt',
    path: consts.SLUG_REQUEST_INFO,
    aliasOf: 'request_data',
    platforms: [PLATFORM_WEB],
  },
  {
    slug: 'request_data',
    label: 'Request Data',
    title: 'Information Request - Tenant Portal',
    path: `${consts.SLUG_CONTACT}${consts.SLUG_REQUEST_INFO}`,
    platforms: [PLATFORM_WEB],
  },
  {
    slug: 'not_found',
    label: 'Not Found',
    path: '*',
    platforms: [PLATFORM_WEB],
  },
];
